import React from "react";
import { classNames } from "components/utils";
import { LaunchableDeployButton } from "./LaunchableDeployButton";
import { PersistentDeployAction } from "./PersistentDeployAction";
import { useLaunchableDeployment } from "../hooks/useLaunchableDeployment";

interface LaunchableDeployActionProps {
  heading?: React.ReactNode;
  launchableName?: string;
}

export const LaunchableDeployAction: React.FC<LaunchableDeployActionProps> = ({
  heading,
  launchableName,
}) => {
  const {
    stickyEstimatedCost,
    estimatedCost,
    providerName,
    loading,
    instanceLoading,
    isPublic,
    nameErrorMessage,
    handleDeployRequest,
    deployLabel,
  } = useLaunchableDeployment();
  const disclosure = (
    <p>
      By deploying, you consent to sharing personal and deployment data with{" "}
      {providerName || "the cloud provider"} as described{" "}
      <a
        href="https://www.nvidia.com/en-us/agreements/cloud-services/service-specific-terms-for-brev/"
        target="_blank"
        rel="noreferrer"
        className="text-highlight underline underline-offset-2"
      >
        here
      </a>
      .
    </p>
  );

  return (
    <PersistentDeployAction>
      {(isPinned) => (
        <div
          className={classNames(
            isPinned
              ? "grid grid-cols-1 items-center gap-x-5 gap-y-2 bg-zinc-950/95 px-5 py-3 sm:px-6 lg:flex"
              : "grid bg-transparent lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start lg:space-x-4",
          )}
        >
          {(heading || (isPinned && launchableName)) && (
            <header
              className={classNames(
                "min-w-0",
                isPinned
                  ? "col-start-1 row-start-1 flex items-center lg:max-w-[45%] lg:shrink-0"
                  : "row-start-1 py-5 lg:col-start-1",
              )}
            >
              {isPinned ? (
                <div className="flex min-w-0 items-center gap-2">
                  <p
                    className="min-w-0 truncate text-base font-semibold leading-6 tracking-tight text-primary"
                    title={launchableName}
                  >
                    {launchableName}
                  </p>
                  <span
                    className="hidden shrink-0 text-zinc-600 lg:block"
                    aria-hidden="true"
                  >
                    •
                  </span>
                  <div className="hidden shrink-0 lg:block">
                    {stickyEstimatedCost}
                  </div>
                </div>
              ) : (
                heading
              )}
            </header>
          )}
          {isPinned && (
            <div className="col-start-1 row-start-3 min-w-0 break-words text-center text-xs leading-4 text-secondary lg:flex-1 lg:shrink lg:text-right">
              {disclosure}
            </div>
          )}
          <div
            className={classNames(
              isPinned
                ? "col-start-1 row-start-2 flex shrink-0 flex-col items-center justify-center gap-2 lg:block"
                : "row-start-2 lg:col-start-2 lg:row-start-1",
            )}
          >
            {isPinned && (
              <div className="order-2 shrink-0 lg:hidden">
                {stickyEstimatedCost}
              </div>
            )}
            <div className="order-1">
              <LaunchableDeployButton
                loading={loading}
                instanceLoading={instanceLoading}
                isPublic={isPublic}
                nameErrorMessage={nameErrorMessage}
                onDeploy={handleDeployRequest}
                deployLabel={deployLabel}
                estimatedCost={isPinned ? undefined : estimatedCost}
                disclosure={isPinned ? undefined : disclosure}
                compact={isPinned}
              />
            </div>
          </div>
        </div>
      )}
    </PersistentDeployAction>
  );
};
